/**
 * CancellationLossWarning — global modal that nudges cancelled users to
 * un-cancel before they lose access.
 *
 * Mounted once in `pages/_app.tsx`. Fetches main-subscription state via
 * `getCancellationContext("main")` and decides whether to show:
 *
 *   - User has a Stripe subscription (`stripe_customer` true)
 *   - The subscription is cancelled at period end (`has_pending_cancel`)
 *   - <30 days remain until access ends (or unknown date — we still show)
 *
 * Triggers on two cadences:
 *   1. **Right after login** — exactly once per session. We use
 *      `sessionStorage` because we want it to fire again on every fresh
 *      browser session (login). A `localStorage` flag would let users
 *      dismiss it permanently which defeats the point.
 *   2. **Every Nth page navigation** — keeps it surfacing even if the
 *      user closed it earlier in the session. Counter increments on
 *      Next.js route change events; modal re-opens when counter %
 *      `EVERY_NTH_NAV` === 0 and the user has dismissed it at least
 *      once already.
 *
 * The modal has one obvious primary action: "Keep my subscription"
 * which calls `abortCancellationFlow` and disappears on success.
 * Secondary action is a "Maybe later" / dismiss — we don't make it
 * impossible to close, just inconvenient enough that they see it
 * regularly until the cancellation is actually reversed (or runs out).
 */
import { FC, useCallback, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useRecoilValue } from "recoil";
import dayjs from "dayjs";

import {
    abortCancellationFlow,
    CancellationContext,
    getCancellationContext,
} from "@/controllers/cancellationFlow";
import { textColorState, userState } from "@/lib/store";

/** Show the modal again after this many post-dismissal navigations. */
const EVERY_NTH_NAV = 5;
/** Only fire if access ends within this many days. */
const DAYS_THRESHOLD = 30;

/** Session storage keys. Per-session is intentional — we want a fresh
 *  fire on every login, not a permanent dismissal flag. */
const SS_KEY_SHOWN_THIS_SESSION = "cancelLossWarning:shownThisSession";
const SS_KEY_NAV_COUNTER = "cancelLossWarning:navCounter";
/** Cache key for the cancellation context payload itself. The endpoint
 *  hits Stripe twice per call (subscription + pricing), so without this
 *  cache every full page load — and there are many in this app —
 *  triggered two Stripe round-trips just to decide whether to nag a user
 *  who in 99% of cases isn't even cancelled. */
const SS_KEY_CONTEXT_CACHE = "cancelLossWarning:contextCache";
/** How long a cached context stays fresh. Long enough to cover a normal
 *  browsing session, short enough that state changes (a cancel confirmed
 *  in another tab, support reversing one in the backoffice) reach the
 *  user within the same session. */
const CONTEXT_CACHE_TTL_MS = 10 * 60 * 1000;

interface CachedContext {
    userId: number;
    cachedAt: number;
    data: CancellationContext;
}

const readCachedContext = (userId: number): CancellationContext | null => {
    try {
        const raw = sessionStorage.getItem(SS_KEY_CONTEXT_CACHE);
        if (!raw) return null;
        const entry = JSON.parse(raw) as CachedContext;
        if (entry.userId !== userId) return null;
        if (Date.now() - entry.cachedAt > CONTEXT_CACHE_TTL_MS) return null;
        return entry.data;
    } catch {
        return null;
    }
};

const writeCachedContext = (userId: number, data: CancellationContext) => {
    try {
        const entry: CachedContext = { userId, cachedAt: Date.now(), data };
        sessionStorage.setItem(SS_KEY_CONTEXT_CACHE, JSON.stringify(entry));
    } catch {
        // sessionStorage blocked — fall through; the next mount just
        // refetches, which is the pre-cache behaviour.
    }
};

const clearCachedContext = () => {
    try {
        sessionStorage.removeItem(SS_KEY_CONTEXT_CACHE);
    } catch {
        // ignore
    }
};

const daysUntil = (epochMs: number | null): number | null => {
    if (!epochMs) return null;
    const days = dayjs(epochMs).diff(dayjs(), "day");
    return Number.isFinite(days) ? days : null;
};

const CancellationLossWarning: FC = () => {
    const user = useRecoilValue(userState);
    const textColor = useRecoilValue(textColorState);
    const router = useRouter();

    const [ctx, setCtx] = useState<CancellationContext | null>(null);
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Fetch the user's main-subscription cancellation state when a
    // logged-in user is detected, reusing a cached payload across mounts
    // within the same session. Without the cache, every full page load
    // (and this app has many) triggered two Stripe round-trips on the
    // backend, even though for the vast majority of users the result is
    // "no pending cancel, nothing to show". Skipping for visitors /
    // not-yet-loaded user state to avoid an authenticated request that
    // would 401.
    useEffect(() => {
        const skip =
            !user
            || !user.subscription_plan
            || user.subscription_plan === "ORTEX Visitor"
            || user.subscription_plan === "ORTEX Free";
        if (skip) return;
        const cached = readCachedContext(user.user_id);
        if (cached) {
            setCtx(cached);
            return;
        }
        let cancelled = false;
        getCancellationContext("main")
            .then((data) => {
                if (cancelled) return;
                setCtx(data);
                writeCachedContext(user.user_id, data);
            })
            .catch(() => {
                // Silently swallow — failing to load the cancel context
                // shouldn't impact the rest of the app.
            });
        return () => {
            cancelled = true;
        };
    }, [user?.subscription_plan, user?.user_id]);

    // Decide whether the conditions for showing are met.
    const shouldShow = useCallback((): boolean => {
        if (!ctx) return false;
        if (!ctx.stripe_customer) return false;
        if (!ctx.has_pending_cancel) return false;
        const days = daysUntil(ctx.cancel_at);
        if (days === null) {
            // Unknown date — be conservative and still show. A user
            // who's cancelled but we don't know when access ends is
            // exactly who needs the most prominent reminder.
            return true;
        }
        return days <= DAYS_THRESHOLD && days >= 0;
    }, [ctx]);

    // Trigger 1: open immediately after the user lands (post-login or
    // first paint of the app), once per session, when conditions are met.
    useEffect(() => {
        if (!shouldShow()) return;
        try {
            const alreadyShown = sessionStorage.getItem(SS_KEY_SHOWN_THIS_SESSION) === "1";
            if (alreadyShown) return;
            sessionStorage.setItem(SS_KEY_SHOWN_THIS_SESSION, "1");
            sessionStorage.setItem(SS_KEY_NAV_COUNTER, "0");
            setOpen(true);
        } catch {
            // sessionStorage can be blocked (private browsing edge case).
            // Fall back to "show once per page-load" — better than never.
            setOpen(true);
        }
    }, [shouldShow]);

    // Trigger 2: re-open every Nth navigation after the user has
    // dismissed once. Resets the counter on open so we don't re-fire
    // immediately on the next route change.
    useEffect(() => {
        const onRouteChange = () => {
            if (!shouldShow()) return;
            try {
                const raw = sessionStorage.getItem(SS_KEY_NAV_COUNTER) || "0";
                const next = Number.parseInt(raw, 10) + 1;
                sessionStorage.setItem(SS_KEY_NAV_COUNTER, String(next));
                if (next > 0 && next % EVERY_NTH_NAV === 0) {
                    setOpen(true);
                }
            } catch {
                // sessionStorage unavailable — skip the cadence trigger,
                // the post-login one already fired.
            }
        };
        router.events.on("routeChangeComplete", onRouteChange);
        return () => router.events.off("routeChangeComplete", onRouteChange);
    }, [router.events, shouldShow]);

    const handleAbort = async () => {
        if (!ctx) return;
        setSubmitting(true);
        try {
            const res = await abortCancellationFlow({
                productKind: "main",
                flowEventId: ctx.flow_event_id,
            });
            if (res.status === "success") {
                // Refresh local context so the modal stays closed and
                // any other consumer of this controller (Settings) sees
                // the new state on next mount.
                setCtx({ ...ctx, has_pending_cancel: false, cancel_at: null });
                setOpen(false);
                // Invalidate the cached context — sessionStorage survives
                // the reload, so without this the next mount would read
                // back the stale `has_pending_cancel: true` payload and
                // re-open the modal the user just resolved.
                clearCachedContext();
                // Optimistic page reload — Settings page is the most
                // likely landing surface and a refresh ensures every
                // bit of UI that gates on `has_cancelled` is in sync.
                if (typeof window !== "undefined") {
                    window.location.reload();
                }
            }
        } catch {
            // Leave the modal open so the user can retry; no error
            // banner here to avoid double-noise.
        } finally {
            setSubmitting(false);
        }
    };

    const handleDismiss = () => {
        setOpen(false);
        // Reset the nav counter so the next "every Nth nav" cadence
        // restarts from 0 from this dismissal point. Without this the
        // counter would keep growing and the user could go many
        // navigations between reminders.
        try {
            sessionStorage.setItem(SS_KEY_NAV_COUNTER, "0");
        } catch {
            // ignore
        }
    };

    if (!ctx) return null;

    const days = daysUntil(ctx.cancel_at);
    const dateText = ctx.cancel_at
        ? dayjs(ctx.cancel_at).format("D MMMM YYYY")
        : null;

    return (
        <Dialog
            open={open}
            onClose={handleDismiss}
            maxWidth="sm"
            fullWidth
            // Prevent backdrop-click from being a quick "close and forget"
            // — this is a moderate-friction prompt by design. Escape key
            // still works via onClose, which is fine.
            disableEscapeKeyDown={false}
        >
            <DialogTitle sx={{ fontWeight: 700, fontSize: "1.4rem", color: textColor }}>
                {days !== null && days <= 7
                    ? `${user?.first_name || "Hi"}: your ORTEX access ends in ${days === 0 ? "today" : days === 1 ? "1 day" : `${days} days`}`
                    : `${user?.first_name || "Hi"}: you're about to lose your ORTEX access`}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ color: textColor, lineHeight: 1.6, marginBottom: 2 }}>
                    {dateText
                        ? `Your subscription is set to end on ${dateText}.`
                        : "Your subscription is set to end at the end of the current billing period."}{" "}
                    After that you'll lose live short interest data, the AI
                    assistant, Market Intelligence, and everything else
                    you've been using.
                </Typography>
                <Typography variant="body2" sx={{ color: textColor, lineHeight: 1.6, marginBottom: 1, opacity: 0.85 }}>
                    You can reverse the cancellation with one click, 
                    nothing is charged, your subscription continues exactly
                    as it was.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "space-between", padding: 2 }}>
                <Button
                    onClick={handleDismiss}
                    disabled={submitting}
                    sx={{ textTransform: "none", color: textColor, opacity: 0.6 }}
                >
                    Maybe later
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleAbort}
                    disabled={submitting}
                    sx={{ textTransform: "none", fontWeight: 600, padding: "10px 28px" }}
                >
                    {submitting ? <CircularProgress size={20} /> : "Keep my subscription"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CancellationLossWarning;
